import { useState, useCallback } from 'react';

/**
 * Custom hook for managing modal dialogs
 * @returns {Object} Modal management functions and state
 */
export const useModal = () => {
  const [infoModal, setInfoModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  /**
   * Show an info modal
   * @param {Object} options
   * @param {string} options.title - Modal title
   * @param {string} options.message - Modal message
   * @param {string} options.type - Modal type (info, success, warning, error)
   * @param {string} options.buttonLabel - Button label
   */
  const showInfo = useCallback(({ title = 'Information', message, type = 'info', buttonLabel = 'OK' }) => {
    setInfoModal({
      title,
      message,
      type,
      buttonLabel
    });
  }, []);

  /**
   * Show a confirmation modal
   * @param {Object} options
   * @param {string} options.title - Modal title
   * @param {string} options.message - Modal message
   * @param {string} options.type - Modal type (info, warning, danger, success)
   * @param {string} options.confirmLabel - Confirm button label
   * @param {string} options.cancelLabel - Cancel button label
   * @param {Function} options.onConfirm - Function to call on confirm
   */
  const showConfirm = useCallback(({ 
    title = 'Confirm Action', 
    message, 
    type = 'info', 
    confirmLabel = 'Confirm', 
    cancelLabel = 'Cancel',
    onConfirm 
  }) => {
    return new Promise((resolve) => {
      setConfirmModal({
        title,
        message,
        type,
        confirmLabel,
        cancelLabel,
        onConfirm: () => {
          setConfirmModal(null);
          if (onConfirm) onConfirm();
          resolve(true);
        },
        onCancel: () => {
          setConfirmModal(null);
          resolve(false);
        }
      });
    });
  }, []);

  /**
   * Close info modal
   */
  const closeInfo = useCallback(() => {
    setInfoModal(null);
  }, []);

  /**
   * Close confirmation modal
   */
  const closeConfirm = useCallback(() => {
    setConfirmModal(null);
  }, []);

  return {
    infoModal,
    confirmModal,
    showInfo,
    showConfirm,
    closeInfo,
    closeConfirm
  };
};

export default useModal;
